import { AxiosError, isAxiosError } from 'axios'

import {
  DEFAULT_MIN_SEND_AMOUNT,
  DEFAULT_MAX_SEND_AMOUNT,
} from './feeCalculations'

export const getErrorMessage = (error: unknown): string | null => {
  if (isAxiosError(error)) {
    const data = (error as AxiosError<string | { message?: string }>).response
      ?.data
    if (typeof data === 'string') return data
    if (data && typeof data === 'object' && 'message' in data) {
      return data.message ?? null
    }
  }
  if (error instanceof Error) return error.message
  return null
}

export const CPN_ERROR_CODES = {
  BFI_UNAVAILABLE: 'BFI_UNAVAILABLE',
  QUOTE_VALIDATION: 'QUOTE_VALIDATION',
  AMOUNT_OUT_OF_RANGE: 'AMOUNT_OUT_OF_RANGE',
  UNSUPPORTED_USE_CASE: 'UNSUPPORTED_USE_CASE',
} as const

export type CpnErrorCode =
  (typeof CPN_ERROR_CODES)[keyof typeof CPN_ERROR_CODES]

export type CpnErrorWithResponse = Error & {
  response?: {
    data?: { code?: string; limits?: { min: number; max: number } }
  }
}

const CPN_ERROR_PATTERNS: Record<CpnErrorCode, string> = {
  [CPN_ERROR_CODES.BFI_UNAVAILABLE]:
    'No bfis available to create requested quote',
  [CPN_ERROR_CODES.QUOTE_VALIDATION]: 'Quote validation error',
  [CPN_ERROR_CODES.AMOUNT_OUT_OF_RANGE]: 'outside range',
  [CPN_ERROR_CODES.UNSUPPORTED_USE_CASE]:
    'does not support the specified use case',
}

const CPN_ERROR_MESSAGES: Record<CpnErrorCode, string> = {
  [CPN_ERROR_CODES.BFI_UNAVAILABLE]: 'Temporarily Unavailable.',
  [CPN_ERROR_CODES.QUOTE_VALIDATION]: 'Quote validation failed',
  [CPN_ERROR_CODES.AMOUNT_OUT_OF_RANGE]: `Amount must be between $${DEFAULT_MIN_SEND_AMOUNT} and $${DEFAULT_MAX_SEND_AMOUNT.toLocaleString()} CAD`,
  [CPN_ERROR_CODES.UNSUPPORTED_USE_CASE]:
    'This payment route is currently unavailable. Please try a different currency or payment method.',
}

type AmountOutOfRangeErrorResponse = Error & {
  response?: {
    data?: {
      code?: string
      recipientLimits?: { min: number; max: number; currency: string }
    }
  }
}

type GetAmountOutOfRangeErrorDataParams = {
  quoteError: Error | null | undefined
  exchangeRate: number
  minSendAmount: number
  maxSendAmount: number
  receiveCurrency: string
  recipientLimitsFromSuccess?: {
    min: number
    max: number
    currency: string
  } | null
}

type AmountOutOfRangeErrorData = {
  isAmountOutOfRangeError: boolean
  minRecipientAmount: number
  maxRecipientAmount: number
  recipientLimitCurrency: string
  hasRecipientLimits: boolean
}

export const getAmountOutOfRangeErrorData = ({
  quoteError,
  exchangeRate,
  minSendAmount,
  maxSendAmount,
  receiveCurrency,
  recipientLimitsFromSuccess,
}: GetAmountOutOfRangeErrorDataParams): AmountOutOfRangeErrorData => {
  const errorResponse = quoteError as AmountOutOfRangeErrorResponse
  const isAmountOutOfRangeError =
    !!quoteError &&
    errorResponse?.response?.data?.code === CPN_ERROR_CODES.AMOUNT_OUT_OF_RANGE

  const recipientLimitsFromError =
    errorResponse?.response?.data?.recipientLimits
  const recipientLimits = recipientLimitsFromSuccess ?? recipientLimitsFromError

  const computedMin =
    exchangeRate > 0 ? Math.ceil(minSendAmount * exchangeRate) : 0
  const computedMax =
    exchangeRate > 0 ? Math.floor(maxSendAmount * exchangeRate) : 0

  const minRecipientAmount = recipientLimits?.min ?? computedMin
  const maxRecipientAmount = recipientLimits?.max ?? computedMax
  const recipientLimitCurrency = recipientLimits?.currency ?? receiveCurrency

  const hasValidRange = minRecipientAmount > 0 && maxRecipientAmount > 0
  const hasRecipientLimits =
    hasValidRange &&
    (recipientLimits != null || (exchangeRate > 0 && !!receiveCurrency))

  return {
    isAmountOutOfRangeError,
    minRecipientAmount,
    maxRecipientAmount,
    recipientLimitCurrency,
    hasRecipientLimits,
  }
}

export const getCpnErrorCode = (errorMessage: string): CpnErrorCode | null => {
  for (const [code, pattern] of Object.entries(CPN_ERROR_PATTERNS)) {
    if (errorMessage.includes(pattern)) {
      return code as CpnErrorCode
    }
  }
  return null
}

export const getCpnErrorMessage = (code: CpnErrorCode): string => {
  return CPN_ERROR_MESSAGES[code]
}

type AmountLimits = {
  min: number
  max: number
  currency?: string
}

type FormatCpnErrorParams = {
  errorMessage: string
  limits?: AmountLimits
}

const formatAmountRangeMessage = (limits: AmountLimits): string =>
  `Amount must be between $${limits.min} and $${limits.max.toLocaleString()} CAD`

/**
 * Formats CPN error messages for better user experience
 * Prefers structured limits from API when available; falls back to defaults for range errors
 */
export const formatCpnErrorMessage = ({
  errorMessage,
  limits,
}: FormatCpnErrorParams): string => {
  if (limits) {
    return formatAmountRangeMessage(limits)
  }

  const errorCode = getCpnErrorCode(errorMessage)
  if (errorCode) {
    return getCpnErrorMessage(errorCode)
  }

  if (
    errorMessage.includes('outside range') ||
    errorMessage.includes('outside our supported limits')
  ) {
    return formatAmountRangeMessage({
      min: DEFAULT_MIN_SEND_AMOUNT,
      max: DEFAULT_MAX_SEND_AMOUNT,
    })
  }

  if (errorMessage.includes('Failed to get CPN quotes:')) {
    return errorMessage.replace('Failed to get CPN quotes: ', '')
  }

  // Shorten very long error messages
  if (errorMessage.length > 50) {
    return 'Amount validation failed - try a smaller amount'
  }

  // Return original message if no transformations apply
  return errorMessage
}
